
import { useNewEventStore } from '@/stores/newEventStore';
import { getActivePinia } from 'pinia';

const STORAGE_KEY_PREFIX = 'rescued_state_';
const RESTORATION_FLAG_KEY = '__state_restoration_in_progress__';

export function isRestoringState(): boolean {
  return sessionStorage.getItem(RESTORATION_FLAG_KEY) === 'true';
}

/**
 * Saves critical state to sessionStorage so it can be restored after page reload.
 * Used for version updates during event creation flow.
 * @param currentRouteName - The name of the current route (must be passed from component context)
 */
export function saveCriticalState(currentRouteName?: string | symbol) {
  try {
    const activePinia = getActivePinia();
    
    if (!activePinia) {
        console.warn('❌ [SAVE] Cannot rescue state: Pinia is not active');
        return;
    }
    
    const newEventStore = useNewEventStore();
    
    const stateToSave = {
        newEvent: JSON.parse(JSON.stringify(newEventStore.newEvent)),
        isNewEvent: newEventStore.isNewEvent,
        organizerId: newEventStore.organizerId,
        organizerPartitionKey: newEventStore.organizerPartitionKey,
        currentRoute: currentRouteName || 'createEventStep1'
    };
    
    const key = `${STORAGE_KEY_PREFIX}newEvent`;
    sessionStorage.setItem(key, JSON.stringify(stateToSave));
    console.log(`✅ [STATE RESCUE] Critical state saved to ${key}`);
    
  } catch (error) {
    console.error('❌ [SAVE] Failed to rescue state:', error);
  }
}

export function restoreCriticalState() {
  try {
    const key = `${STORAGE_KEY_PREFIX}newEvent`;
    const saved = sessionStorage.getItem(key);
    
    if (!saved) {
        return;
    }

    // Set flag IMMEDIATELY before any operations
    sessionStorage.setItem(RESTORATION_FLAG_KEY, 'true');
    console.log('📋 [STATE RESCUE] Found saved state, attempting restore...');
    const parsed = JSON.parse(saved);
    const newEventStore = useNewEventStore();
    
    if (parsed.newEvent) {
        // JSON.parse leaves dates as strings. We must manually hydrate them back to Date objects.
        const dateFields = ['start', 'end', 'created', 'updated', 'publish', 'cancelledAt'];
        
        dateFields.forEach(field => {
            if (parsed.newEvent[field] && typeof parsed.newEvent[field] === 'string') {
                parsed.newEvent[field] = new Date(parsed.newEvent[field]);
            }
        });

        // Deep merge to preserve reactivity in Pinia
        Object.keys(parsed.newEvent).forEach(key => {
            newEventStore.newEvent[key as any] = parsed.newEvent[key];
        });

        if (parsed.organizerId !== undefined) {
            newEventStore.organizerId = parsed.organizerId;
        }

        if (parsed.organizerPartitionKey !== undefined) {
            newEventStore.organizerPartitionKey = parsed.organizerPartitionKey;
        }

        if (typeof parsed.isNewEvent === 'boolean') {
            newEventStore.isNewEvent = parsed.isNewEvent;
        }

        if (newEventStore.newEvent.id && newEventStore.newEvent.id !== 0) {
            newEventStore.isNewEvent = false;
        }

        console.log('✨ [STATE RESCUE] Critical state restored:', {
            title: newEventStore.newEvent.title,
            typeId: newEventStore.newEvent.typeId,
            creatorId: newEventStore.newEvent.creatorId,
            creatorPartitionKey: newEventStore.newEvent.creatorPartitionKey,
            hasImage: !!newEventStore.newEvent.image,
            tagsCount: newEventStore.newEvent.tags?.length || 0,
            eventId: newEventStore.newEvent.id,
            isNewEvent: newEventStore.isNewEvent,
        });
    }
    
    // DON'T clean up yet - let the mounted hook handle it
    // This ensures guards see the flag even if auth initialization is slow
    console.log('🕐 [STATE RESCUE] Flag will be cleaned up after first route navigation completes');
  } catch (error) {
    console.error('❌ [STATE RESCUE] Failed to restore state:', error);
    sessionStorage.removeItem(RESTORATION_FLAG_KEY);
  }
}

export function clearRestorationFlag() {
  const key = `${STORAGE_KEY_PREFIX}newEvent`;
  sessionStorage.removeItem(RESTORATION_FLAG_KEY);
  sessionStorage.removeItem(key);
  console.log('🧹 [STATE RESCUE] Cleanup: Restoration flag and saved state cleared');
}
